import React, { useState, useEffect } from 'react';
import { Shield, Save, Users, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { userAPI } from '../../services/api';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface RolePermissions {
  role: string;
  permissions: Set<string>;
}

const AccessControl: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const { toasts, removeToast, success, error, warning } = useToast();

  // Define all available permissions
  const permissions: Permission[] = [
    // Dashboard
    { id: 'view_dashboard', name: 'View Dashboard', description: 'Access dashboard and statistics', category: 'Dashboard' },
    
    // Enquiries
    { id: 'view_enquiries', name: 'View Enquiries', description: 'View all enquiries', category: 'Enquiries' },
    { id: 'create_enquiry', name: 'Create Enquiry', description: 'Add new enquiries', category: 'Enquiries' },
    { id: 'edit_enquiry', name: 'Edit Enquiry', description: 'Modify existing enquiries', category: 'Enquiries' },
    { id: 'delete_enquiry', name: 'Delete Enquiry', description: 'Remove enquiries', category: 'Enquiries' },
    { id: 'convert_enquiry', name: 'Convert to Member', description: 'Convert enquiry to membership', category: 'Enquiries' },
    
    // Members
    { id: 'view_members', name: 'View Members', description: 'View all members', category: 'Members' },
    { id: 'edit_member', name: 'Edit Member', description: 'Modify member details', category: 'Members' },
    { id: 'delete_member', name: 'Delete Member', description: 'Remove members', category: 'Members' },
    { id: 'view_receipts', name: 'View Receipts', description: 'View payment receipts', category: 'Members' },
    { id: 'resend_receipt', name: 'Resend Receipt', description: 'Resend payment receipts', category: 'Members' },
    
    // Payments
    { id: 'view_payments', name: 'View Payments', description: 'View payment history', category: 'Payments' },
    { id: 'add_payment', name: 'Add Payment', description: 'Record new payments', category: 'Payments' },
    { id: 'upgrade_membership', name: 'Upgrade Membership', description: 'Upgrade member plans', category: 'Payments' },
    
    // Membership Plans
    { id: 'view_plans', name: 'View Plans', description: 'View membership plans', category: 'Plans' },
    { id: 'create_plan', name: 'Create Plan', description: 'Add new plans', category: 'Plans' },
    { id: 'edit_plan', name: 'Edit Plan', description: 'Modify existing plans', category: 'Plans' },
    { id: 'delete_plan', name: 'Delete Plan', description: 'Remove plans', category: 'Plans' },
    
    // Users
    { id: 'view_users', name: 'View Users', description: 'View all users', category: 'Users' },
    { id: 'create_user', name: 'Create User', description: 'Add new users', category: 'Users' },
    { id: 'edit_user', name: 'Edit User', description: 'Modify user details', category: 'Users' },
    { id: 'delete_user', name: 'Delete User', description: 'Remove users', category: 'Users' },
    { id: 'manage_roles', name: 'Manage Roles', description: 'Assign/change user roles', category: 'Users' },
    
    // Reports
    { id: 'view_reports', name: 'View Reports', description: 'Access sales reports', category: 'Reports' },
    { id: 'export_reports', name: 'Export Reports', description: 'Download reports as Excel', category: 'Reports' },
    
    // Activity
    { id: 'view_activity', name: 'View Activity', description: 'View system activity logs', category: 'Activity' },
    
    // Access Control
    { id: 'view_access_control', name: 'View Access Control', description: 'Access permission management page', category: 'Access Control' },
  ];

  // Default permissions for each role
  // Member role removed - will be handled by separate mobile app
  const defaultRolePermissions: { [key: string]: string[] } = {
    'Admin': permissions.map(p => p.id), // All permissions
    'Trainer': [
      'view_dashboard',
      'view_enquiries',
      'view_members',
      'view_receipts',
      'view_activity',
    ],
    'Receptionist': [
      'view_dashboard',
      'view_enquiries',
      'create_enquiry',
      'edit_enquiry',
      'convert_enquiry',
      'view_members',
      'edit_member',
      'view_receipts',
      'resend_receipt',
      'view_payments',
      'add_payment',
      'view_plans',
      'view_activity',
    ],
  };

  const [rolePermissions, setRolePermissions] = useState<{ [key: string]: Set<string> }>({
    'Admin': new Set(defaultRolePermissions['Admin']),
    'Trainer': new Set(defaultRolePermissions['Trainer']),
    'Receptionist': new Set(defaultRolePermissions['Receptionist']),
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await userAPI.getAll();
      setUsers(data);
    } catch (err: any) {
      error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (role: string, permissionId: string) => {
    setRolePermissions(prev => {
      const newPerms = new Map<string, Set<string>>(
        Object.entries(prev).map(([k, v]) => [k, v as Set<string>])
      );
      const rolePerms = new Set<string>(newPerms.get(role) || new Set<string>());
      
      if (rolePerms.has(permissionId)) {
        rolePerms.delete(permissionId);
      } else {
        rolePerms.add(permissionId);
      }
      
      newPerms.set(role, rolePerms);
      return Object.fromEntries(Array.from(newPerms.entries()));
    });
  };

  const savePermissions = () => {
    success('Permissions saved successfully!');
    warning('Note: Permission changes will take effect on next login');
  };

  const handleEditUserRole = (user: any) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUserRole = async (newRole: string) => {
    if (!selectedUser) return;
    
    try {
      await userAPI.updateRole(selectedUser.id, newRole);
      success(`User role updated to ${newRole}`);
      setShowEditModal(false);
      fetchUsers();
    } catch (err: any) {
      console.error('Failed to update user role:', err);
      error(`Failed to update user role: ${err.response?.data?.message || err.message}`);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return { bg: '#FEE2E2', text: '#DC2626', border: '#DC2626' };
      case 'receptionist':
        return { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' };
      case 'trainer':
        return { bg: '#D1FAE5', text: '#065F46', border: '#10B981' };
      case 'member':
        return { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' };
      default:
        return { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' };
    }
  };

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as { [key: string]: Permission[] });

  // Member role removed - will be handled by separate mobile app
  const roles = ['Admin', 'Receptionist', 'Trainer'];

  if (loading) {
    return <div className="loading">Loading access control...</div>;
  }

  return (
    <div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="header">
        <h1 className="header-title">
          <Shield size={28} style={{ marginRight: '0.5rem' }} />
          Access Control
        </h1>
      </div>

      {/* Role Permissions Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 className="card-title">Role Permissions</h2>
          <button
            onClick={savePermissions}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Save size={18} />
            Save Changes
          </button>
        </div>

        <div className="card-body">
          <div style={{
            background: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={20} color="#92400E" />
            <span style={{ color: '#92400E', fontSize: '0.9rem' }}>
              Changes to permissions will take effect when users log in next time.
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem'
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{
                    padding: '1rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: '#374151',
                    minWidth: '300px'
                  }}>
                    Permission
                  </th>
                  {roles.map(role => {
                    const colors = getRoleBadgeColor(role);
                    return (
                      <th key={role} style={{
                        padding: '1rem',
                        textAlign: 'center',
                        fontWeight: '700',
                      }}>
                        <div style={{
                          display: 'inline-block',
                          backgroundColor: colors.bg,
                          color: colors.text,
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          border: `1px solid ${colors.border}`
                        }}>
                          {role}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {Object.entries(permissionsByCategory).map(([category, perms]) => (
                  <React.Fragment key={category}>
                    <tr>
                      <td colSpan={roles.length + 1} style={{
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '0.95rem'
                      }}>
                        {category}
                      </td>
                    </tr>
                    {perms.map(perm => (
                      <tr key={perm.id} style={{
                        borderBottom: '1px solid #F3F4F6',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <td style={{ padding: '1rem' }}>
                          <div style={{ fontWeight: '600', color: '#1F2937', marginBottom: '0.25rem' }}>
                            {perm.name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                            {perm.description}
                          </div>
                        </td>
                        {roles.map(role => {
                          const hasPermission = rolePermissions[role]?.has(perm.id);
                          const isAdmin = role === 'Admin';
                          return (
                            <td key={role} style={{ padding: '1rem', textAlign: 'center' }}>
                              <button
                                onClick={() => !isAdmin && togglePermission(role, perm.id)}
                                disabled={isAdmin}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: isAdmin ? 'not-allowed' : 'pointer',
                                  padding: '0.5rem',
                                  opacity: isAdmin ? 0.5 : 1,
                                  transition: 'transform 0.2s'
                                }}
                                onMouseEnter={(e) => !isAdmin && (e.currentTarget.style.transform = 'scale(1.2)')}
                                onMouseLeave={(e) => !isAdmin && (e.currentTarget.style.transform = 'scale(1)')}
                              >
                                {hasPermission ? (
                                  <CheckCircle size={24} color="#10B981" />
                                ) : (
                                  <XCircle size={24} color="#EF4444" />
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Users and Their Roles Section */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            <Users size={24} style={{ marginRight: '0.5rem' }} />
            Users & Roles
          </h2>
        </div>

        <div className="card-body">
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Current Role</th>
                  <th>Assigned Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  // Extract role - ensure it's a clean string
                  let userRole = 'Member';
                  if (user.roles) {
                    if (Array.isArray(user.roles) && user.roles.length > 0) {
                      userRole = String(user.roles[0]).trim();
                    } else if (typeof user.roles === 'string') {
                      userRole = user.roles.trim();
                    }
                  }
                  
                  const permCount = rolePermissions[userRole]?.size || 0;
                  const colors = getRoleBadgeColor(userRole);
                  
                  return (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.firstName} {user.lastName}</strong>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          backgroundColor: colors.bg,
                          color: colors.text,
                          padding: '0.4rem 0.8rem',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          border: `1px solid ${colors.border}`
                        }}>
                          {userRole}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          color: '#667eea',
                          fontWeight: '600'
                        }}>
                          {permCount} permissions
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleEditUserRole(user)}
                          style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                        >
                          Change Role
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit User Role Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Change User Role</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ color: '#6B7280', marginBottom: '0.5rem' }}>User:</p>
                <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1F2937' }}>
                  {selectedUser.firstName} {selectedUser.lastName}
                </p>
                <p style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>{selectedUser.email}</p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  Select New Role:
                </label>
                {roles.map(role => {
                  const colors = getRoleBadgeColor(role);
                  const currentRole = selectedUser.roles?.[0] || 'Member';
                  const isCurrentRole = role === currentRole;

                  return (
                    <button
                      key={role}
                      onClick={() => !isCurrentRole && handleUpdateUserRole(role)}
                      disabled={isCurrentRole}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '1rem',
                        marginBottom: '0.75rem',
                        border: `2px solid ${isCurrentRole ? colors.border : '#E5E7EB'}`,
                        borderRadius: '8px',
                        background: isCurrentRole ? colors.bg : 'white',
                        color: isCurrentRole ? colors.text : '#374151',
                        cursor: isCurrentRole ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        if (!isCurrentRole) {
                          e.currentTarget.style.borderColor = colors.border;
                          e.currentTarget.style.background = colors.bg;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCurrentRole) {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{role}</span>
                        {isCurrentRole && (
                          <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>(Current)</span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', opacity: 0.8 }}>
                        {rolePermissions[role]?.size || 0} permissions
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControl;

